import moment from 'moment';

import {
  getActiveUserNumberByRange,
  getCourseStatisticsByDate,
  getCourseStatisticsBySchool,
  getUserActiveNumByDate,
} from '../services/statistics.js';
import { responseCode, responseData } from '../types/domain.js';
import type {
  ActiveUserDateRow,
  ActiveUserSchoolRow,
  CourseDateRow,
  CourseSchoolRow,
  StatisticsSearchRequest,
} from '../types/domain.js';
import { isRecord } from './dva-types.js';
import type { ModelSaga, TypedModel } from './dva-types.js';

export type StatisticsMenu =
  | 'unique_visitors_by_school'
  | 'unique_visitors_by_time'
  | 'course_arrangement_by_school'
  | 'course_arrangement_by_time';

interface ChartBucket {
  type: string;
  count: number;
}

interface CourseBucket {
  type: string;
  courseCount?: number;
  count?: number;
}

export interface StatisticsState {
  readonly searchRequest: StatisticsSearchRequest;
  readonly selectMenu: StatisticsMenu;
  readonly loading?: boolean;
  readonly uniqueVisitorsBySchool?: ActiveUserSchoolRow[];
  readonly chartData?: readonly (
    ActiveUserDateRow | CourseDateRow | ActiveUserSchoolRow | CourseSchoolRow | ChartBucket
  )[];
  readonly listData?: readonly CourseBucket[];
  readonly totalCount?: number;
}

function percent(count: number, total: number): number {
  return Number.parseInt(String((count / total) * 100000 + 0.5), 10) / 1000;
}

function isStatisticsMenu(value: unknown): value is StatisticsMenu {
  return (
    value === 'unique_visitors_by_school' ||
    value === 'unique_visitors_by_time' ||
    value === 'course_arrangement_by_school' ||
    value === 'course_arrangement_by_time'
  );
}

function isStatisticsState(value: unknown): value is StatisticsState {
  return (
    isRecord(value) &&
    isRecord(value['searchRequest']) &&
    isStatisticsMenu(value['selectMenu']) &&
    (value['loading'] === undefined || typeof value['loading'] === 'boolean')
  );
}

const model: TypedModel<StatisticsState> = {
  namespace: 'statistics',
  state: {
    searchRequest: {
      start: moment().subtract(30, 'days').isBefore(moment('2022-12-24'))
        ? moment('2022-12-24')
        : moment().subtract(30, 'days'),
      end: moment().subtract(1, 'days'),
    },
    selectMenu: 'unique_visitors_by_school',
  },
  subscriptions: {
    setup({ dispatch, history }) {
      history.listen(({ pathname }) => {
        if (pathname === '/statistics') dispatch({ type: 'init' });
      });
    },
  },
  effects: {
    *init({ payload }, { put, select }): ModelSaga {
      const selectedState = yield select((row: { statistics: StatisticsState }) => row.statistics);
      if (!isStatisticsState(selectedState)) {
        throw new TypeError('Invalid statistics state');
      }
      const { selectMenu: stateMenu, searchRequest } = selectedState;
      const input = (payload ?? {}) as Partial<StatisticsState> & StatisticsSearchRequest;
      const payloadObject = typeof payload === 'object' && payload !== null ? payload : {};
      const { selectMenu: payloadMenu, ...otherPayload } = input;
      yield put({ type: 'update', payload: { loading: true } });

      const request: StatisticsSearchRequest = { ...searchRequest, ...otherPayload };
      const requestMutable = request as {
        start?: string | moment.Moment;
        end?: string | moment.Moment;
      };
      if (requestMutable.start)
        requestMutable.start = moment(requestMutable.start).format('YYYY-MM-DD');
      if (requestMutable.end) requestMutable.end = moment(requestMutable.end).format('YYYY-MM-DD');
      const selectMenu = payloadMenu ?? stateMenu;
      yield put({
        type: 'update',
        payload: { selectMenu: selectMenu || 'unique_visitors_by_school' },
      });

      if (selectMenu === 'unique_visitors_by_school') {
        yield put({
          type: 'unique_visitors_by_school',
          payload: { request: requestMutable, ...payloadObject },
        });
      } else if (selectMenu === 'unique_visitors_by_time') {
        yield put({
          type: 'unique_visitors_by_time',
          payload: { request: requestMutable, ...payloadObject },
        });
      } else if (selectMenu === 'course_arrangement_by_school') {
        yield put({
          type: 'course_arrangement_by_school',
          payload: { request: requestMutable, ...payloadObject },
        });
      } else if (selectMenu === 'course_arrangement_by_time') {
        yield put({
          type: 'course_arrangement_by_time',
          payload: { request: requestMutable, ...payloadObject },
        });
      }
    },

    *unique_visitors_by_school({ payload }, { put }): ModelSaga {
      const { request, ...other } = payload as { request: StatisticsSearchRequest } & Record<
        string,
        unknown
      >;
      const response = yield getActiveUserNumberByRange(request);
      if (responseCode(response) !== 0) {
        yield put({ type: 'update', payload: { loading: false } });
        return;
      }
      const data = responseData<ActiveUserSchoolRow[]>(response) ?? [];
      window.statisticsRequest = request;
      const chartData: ChartBucket[] = [
        { type: '0名', count: 0 },
        { type: '1名~500名', count: 0 },
        { type: '501名~1000名', count: 0 },
        { type: '1001名~1500名', count: 0 },
        { type: '1501名~2000名', count: 0 },
      ];
      let totalCount = 0;
      for (const row of data) {
        totalCount += row.count;
        if (row.count === 0) {
          chartData[0]!.count += 1;
          continue;
        }
        const typeNum = Number.parseInt(String((row.count - 1) / 500), 10);
        if (typeNum > 3) {
          if (!chartData[5]) chartData.push({ type: '2001名及以上', count: 0 });
          chartData[5]!.count += 1;
          continue;
        }
        chartData[typeNum + 1]!.count += 1;
      }
      for (const row of chartData)
        row.type = `${row.type}（${row.count}所，${percent(row.count, data.length)}%）`;
      yield put({
        type: 'update',
        payload: {
          ...other,
          loading: false,
          searchRequest: request,
          uniqueVisitorsBySchool: data,
          chartData,
          totalCount,
          stepLoading: false,
        },
      });
    },

    *unique_visitors_by_time({ payload }, { put }): ModelSaga {
      const { request, ...other } = payload as { request: StatisticsSearchRequest } & Record<
        string,
        unknown
      >;
      const response = yield getUserActiveNumByDate(request);
      if (responseCode(response) !== 0) {
        yield put({ type: 'update', payload: { loading: false } });
        return;
      }
      window.statisticsRequest = request;
      yield put({
        type: 'update',
        payload: {
          ...other,
          searchRequest: request,
          chartData: responseData<ActiveUserDateRow[]>(response),
          loading: false,
        },
      });
    },

    *course_arrangement_by_school({ payload }, { put }): ModelSaga {
      const { request, ...other } = payload as { request: StatisticsSearchRequest } & Record<
        string,
        unknown
      >;
      const response = yield getCourseStatisticsBySchool(request);
      if (responseCode(response) !== 0) {
        yield put({ type: 'update', payload: { loading: false } });
        return;
      }
      const data = responseData<CourseSchoolRow[]>(response) ?? [];
      window.statisticsRequest = request;
      const listData: CourseBucket[] = [
        { type: '0节', courseCount: 0 },
        { type: '1节~500节', courseCount: 0 },
        { type: '501节~1000节', courseCount: 0 },
        { type: '1001节~1500节', courseCount: 0 },
        { type: '1501节~2000节', courseCount: 0 },
      ];
      let totalCount = 0;
      for (const row of data) {
        totalCount += row.courseCount;
        if (row.courseCount === 0) {
          listData[0]!.courseCount = (listData[0]!.courseCount ?? 0) + 1;
          continue;
        }
        const typeNum = Number.parseInt(String((row.courseCount - 1) / 500), 10);
        if (typeNum > 3) {
          if (!listData[5]) listData.push({ type: '2001名及以上', count: 0 });
          listData[5]!.courseCount = (listData[5]!.courseCount as number) + 1;
          continue;
        }
        listData[typeNum + 1]!.courseCount = (listData[typeNum + 1]!.courseCount ?? 0) + 1;
      }
      for (const row of listData)
        row.type = `${row.type}（${row.courseCount}所，${percent(row.courseCount as number, data.length)}%）`;
      yield put({
        type: 'update',
        payload: {
          ...other,
          searchRequest: request,
          chartData: data,
          listData,
          loading: false,
          totalCount,
        },
      });
    },

    *course_arrangement_by_time({ payload }, { put }): ModelSaga {
      const { request, ...other } = payload as { request: StatisticsSearchRequest } & Record<
        string,
        unknown
      >;
      const response = yield getCourseStatisticsByDate(request);
      if (responseCode(response) !== 0) {
        yield put({ type: 'update', payload: { loading: false } });
        return;
      }
      window.statisticsRequest = request;
      yield put({
        type: 'update',
        payload: {
          ...other,
          searchRequest: request,
          chartData: responseData<CourseDateRow[]>(response),
          loading: false,
        },
      });
    },
  },
  reducers: {
    update(
      state: StatisticsState,
      { payload }: { payload: Partial<StatisticsState> },
    ): StatisticsState {
      return { ...state, ...payload };
    },
  },
};

export default model;
